import { SearchableVendorTaxonomyKey } from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { getVendorTaxonomyKeyFromVendorType } from '@zola-helpers/client/dist/es/marketplace/vendorUtils';
import { Filter, Sort } from '@zola/tracking-contracts/src/objects';

import _groupBy from 'lodash/groupBy';

import {
  VendorSearchRequestType,
  VendorSearchSorting,
} from '~/pages/couples/explore/components/SearchResults/types/types';
import { LocationOrAllMarketsType } from '~/types/responseTypes';
import { OptionFacetType } from '~/types/types';
import { formatDate } from '~/util/dateUtils';
import { isSearchLocation } from '~/util/searchUtils';

import { filtersByVendorCategory } from '../components/SearchResults/meta/searchCategoriesMeta';
import { sortingByVendorType } from '../components/SearchResults/utils/sortHelpers';

const getFacetListDisplayNames = (facets: OptionFacetType[] | null | undefined) =>
  facets?.map((facet) => facet.displayName);

const getCapacityFacets = (facets: OptionFacetType[] | null | undefined) =>
  getFacetListDisplayNames(facets?.filter((facet) => facet.slug.startsWith('capacity')));

/**
 * Builds the filter payload of the Product List Viewed tracking event used with
 * tracking contracts for an executed search on the search results page.
 *
 * @param searchRequest - The executed search request.  Not the payload to the
 * backend, but rather the internal search request object used on the SRP.
 *
 * @param selectedLocation Which location, if any, is currently being viewed
 * @param selectedFacets From the filters/dropdowns on the page, which facets were used to filter the request.
 *
 * @returns A list of Filters representing how the viewer either actively (picked a filter) or
 * passively (viewed an SRP/City) narrowed the storefronts to what was shown on the page.
 */
export const buildSearchFilterTracking = (
  searchRequest: VendorSearchRequestType,
  selectedLocation?: LocationOrAllMarketsType | null,
  selectedFacets?: OptionFacetType[] | null
): Filter[] => {
  const { awardGroups, metroTypes, vendorType, price, targetDates } = searchRequest;

  const filters: Filter[] = [];

  if (awardGroups?.[0]?.length) {
    filters.push({
      type: 'AWARD_GROUPS',
      value: awardGroups[0],
    });
  }

  if (selectedLocation?.type === 'STATE') {
    filters.push({
      type: 'STATE',
      value: selectedLocation.slug,
    });
  }

  if (selectedLocation?.type === 'MARKET') {
    filters.push({
      type: 'MARKET',
      value: selectedLocation.slug,
    });
  }

  if (selectedLocation?.type === 'CITY') {
    filters.push({
      type: 'CITY_SLUG',
      value: selectedLocation.slug,
    });
  }

  if (vendorType) {
    filters.push({
      type: 'CATEGORY',
      value: vendorType,
    });
  }

  if (price?.priceCents?.min || price?.priceCents?.max) {
    const min = price?.priceCents?.min ? price.priceCents.min / 100 : 'null';
    const max = price?.priceCents?.max ? price.priceCents.max / 100 : 'null';

    filters.push({
      type: 'PRICE',
      value: `${min}_${max}`,
    });
  }

  if (targetDates) {
    filters.push({
      type: 'AVAILABILITY',
      value: targetDates.map((availabilityDate) => `${formatDate(availabilityDate, 'YYYY-MM-DD')}`),
    });
  }

  if (selectedFacets && searchRequest.vendorType) {
    const taxonomyKey = getVendorTaxonomyKeyFromVendorType(
      searchRequest.vendorType
    ) as SearchableVendorTaxonomyKey;
    const groups = _groupBy(selectedFacets, 'parentKey');

    const searchFilters = filtersByVendorCategory(taxonomyKey);

    Object.keys(groups).forEach((parentKey) => {
      const searchFilter = searchFilters.find(({ key }) => key === parentKey);
      if (searchFilter && searchFilter.trackingFilter) {
        filters.push({
          type: searchFilter.trackingFilter,
          value: groups[parentKey].map((facet: OptionFacetType) => facet.key),
        });
      }
    });
  }

  if (metroTypes && metroTypes.length > 0) {
    filters.push({
      type: 'METRO',
      value: metroTypes,
    });
  }

  // we want to track exactly which capacity checkboxes a user selected,
  // (not the min-max values) so we need use the facet lists passed down from the parent
  const capacityFacetsForTracking = getCapacityFacets(selectedFacets);
  if (capacityFacetsForTracking?.length) {
    filters.push({
      type: 'CAPACITY',
      value: capacityFacetsForTracking,
    });
  }

  return filters;
};

/**
 * Builds the sort payload of the Product List Viewed tracking event used with
 * tracking contracts for an executed search on the search results page.
 *
 * @param sort Current sort, note that sort options depend on the category and location
 * @param taxonomyKey Which category, if any, is currently being viewed
 * @param selectedLocation Which location is being viewed
 *
 * @returns A Sort, if any is applicable, for use with the tracking contracts
 * track product list viewed event.
 */
export const buildSearchSortTracking = (
  sort?: VendorSearchSorting | null,
  taxonomyKey?: SearchableVendorTaxonomyKey,
  selectedLocation?: LocationOrAllMarketsType | null
): Sort | undefined => {
  if (sort && (isSearchLocation(selectedLocation) || selectedLocation === undefined)) {
    const sortsInUx = sortingByVendorType(taxonomyKey, selectedLocation);
    const foundSort = sortsInUx.find((x) => x.sortBy === sort.sortBy);

    if (foundSort?.sortBy) {
      return {
        value: foundSort.sortOrder,
        type: foundSort.trackingType,
      };
    }
  }
};
